import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { MessageCircle, Send, X, Loader2, Sparkles, Flag, Check } from "lucide-react";
import { toast } from "sonner";

const SESSION_KEY = "smart_concierge_session_v1";
const HISTORY_KEY = "smart_concierge_history_v1";
const HANDOFF_KEY = "smart_concierge_handoff_v1";

const STARTER_PROMPTS = [
    "What services does SMART offer?",
    "How does booking a cleaner work?",
    "Tell me about VIP tiers",
    "How do I become a pro?",
];


export default function ConciergeChat() {
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem(HISTORY_KEY) || "[]");
        } catch {
            return [];
        }
    });
    const [draft, setDraft] = useState("");
    const [sending, setSending] = useState(false);
    const [sessionId, setSessionId] = useState(() => localStorage.getItem(SESSION_KEY) || null);
    const [handoffDone, setHandoffDone] = useState(() => localStorage.getItem(HANDOFF_KEY) === "1");
    const [handoffOpen, setHandoffOpen] = useState(false);
    const [handoffEmail, setHandoffEmail] = useState("");
    const [handoffPhone, setHandoffPhone] = useState("");
    const [handoffQuestion, setHandoffQuestion] = useState("");
    const [handoffSending, setHandoffSending] = useState(false);
    const endRef = useRef(null);

    useEffect(() => {
        if (open) endRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages.length, open]);

    useEffect(() => {
        try {
            localStorage.setItem(HISTORY_KEY, JSON.stringify(messages.slice(-40)));
        } catch { /* storage disabled */ }
    }, [messages]);

    const send = async (text) => {
        const msg = (text ?? draft).trim();
        if (!msg || sending) return;
        setSending(true);
        setMessages((m) => [...m, { role: "user", content: msg }]);
        setDraft("");
        try {
            const { data } = await api.post("/concierge/chat", {
                message: msg,
                session_id: sessionId,
            });
            if (data.session_id && data.session_id !== sessionId) {
                setSessionId(data.session_id);
                try { localStorage.setItem(SESSION_KEY, data.session_id); } catch { /* storage disabled */ }
            }
            setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
        } catch (e) {
            setMessages((m) => [
                ...m,
                {
                    role: "assistant",
                    content: "I had trouble replying just now. Please try again — or email customercare@smartdemands247.com if urgent.",
                    error: true,
                },
            ]);
        } finally {
            setSending(false);
        }
    };

    const hasChat = messages.length > 0;
    const lastAssistant = [...messages].reverse().find((m) => m.role === "assistant");
    const suggestHandoff = lastAssistant && /customercare@smartdemands247\.com|human|admin|teammate/i.test(lastAssistant.content || "");

    const submitHandoff = async () => {
        if (handoffQuestion.trim().length < 5) {
            toast.error("Please write at least a short note so admin can help.");
            return;
        }
        setHandoffSending(true);
        try {
            await api.post("/concierge/handoff", {
                session_id: sessionId,
                email: handoffEmail.trim() || undefined,
                phone: handoffPhone.trim() || undefined,
                question: handoffQuestion.trim(),
            });
            setHandoffDone(true);
            setHandoffOpen(false);
            try { localStorage.setItem(HANDOFF_KEY, "1"); } catch { /* storage disabled */ }
            toast.success("Ticket raised — a human will reach out shortly.");
            setMessages((m) => [
                ...m,
                {
                    role: "assistant",
                    content: "Ticket raised with the SMART admin team. We'll follow up by email or phone within 24 hours.",
                },
            ]);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't raise the ticket. Try again or email customercare@smartdemands247.com.");
        } finally {
            setHandoffSending(false);
        }
    };

    return (
        <>
            {/* Floating launcher — hidden on narrow screens below bottom nav */}
            {!open && (
                <button
                    data-testid="concierge-launcher"
                    onClick={() => setOpen(true)}
                    aria-label="Open SMART Concierge chat"
                    className="fixed z-[60] bottom-24 right-5 md:bottom-6 md:right-6 w-14 h-14 rounded-full bg-[#FFD700] text-black grid place-items-center shadow-[0_8px_30px_-4px_rgba(255,215,0,0.6)] hover:scale-105 active:scale-95 transition-all"
                >
                    <MessageCircle size={22} strokeWidth={2.2} />
                    <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-emerald-400 animate-pulse" />
                </button>
            )}

            {open && (
                <div
                    data-testid="concierge-panel"
                    className="fixed z-[70] bottom-20 right-3 md:bottom-6 md:right-6 w-[94vw] max-w-sm h-[70vh] max-h-[560px] rounded-2xl border border-[#FFD700]/40 bg-background shadow-2xl flex flex-col overflow-hidden"
                >
                    {/* Header */}
                    <div className="bg-gradient-to-r from-[#1a1203] to-black border-b border-[#FFD700]/30 px-4 py-3 flex items-center justify-between">
                        <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-full bg-[#FFD700] text-black grid place-items-center">
                                <Sparkles size={16} />
                            </div>
                            <div>
                                <div className="text-sm text-foreground font-semibold">Royal Concierge</div>
                                <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-300/80 flex items-center gap-1.5">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Online · 24/7
                                </div>
                            </div>
                        </div>
                        <button
                            data-testid="concierge-close"
                            onClick={() => setOpen(false)}
                            className="w-8 h-8 grid place-items-center rounded-full text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors"
                            aria-label="Close chat"
                        >
                            <X size={18} />
                        </button>
                    </div>

                    {/* Messages */}
                    <div
                        data-testid="concierge-messages"
                        className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-background"
                    >
                        {!hasChat && (
                            <div className="text-center pt-4">
                                <div className="text-xs text-muted-foreground mb-4">
                                    Hi Royal — I can answer questions about services, pricing, booking, VIP tiers and more.
                                </div>
                                <div className="flex flex-wrap gap-2 justify-center">
                                    {STARTER_PROMPTS.map((p) => (
                                        <button
                                            key={p}
                                            data-testid={`concierge-starter-${p.slice(0, 10).replace(/\s/g, "-").toLowerCase()}`}
                                            onClick={() => send(p)}
                                            className="text-[11px] px-3 py-1.5 rounded-full border border-[#FFD700]/40 text-[#FFD700] hover:bg-[#FFD700]/10 transition-colors"
                                        >
                                            {p}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        {messages.map((m, i) => (
                            <div
                                key={`msg-${i}-${m.role}`}
                                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                            >
                                <div
                                    data-testid={`concierge-msg-${m.role}-${i}`}
                                    className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                                        m.role === "user"
                                            ? "bg-[#FFD700] text-black rounded-br-sm"
                                            : m.error
                                            ? "bg-red-950/40 border border-red-700/40 text-red-200 rounded-bl-sm"
                                            : "bg-white/5 border border-white/10 text-foreground rounded-bl-sm"
                                    }`}
                                >
                                    {m.content}
                                </div>
                            </div>
                        ))}
                        {sending && (
                            <div className="flex justify-start">
                                <div className="px-3.5 py-2.5 rounded-2xl bg-white/5 border border-white/10 text-xs text-muted-foreground inline-flex items-center gap-2">
                                    <Loader2 size={14} className="animate-spin" /> Thinking...
                                </div>
                            </div>
                        )}
                        <div ref={endRef} />
                    </div>

                    {/* Composer */}
                    {handoffOpen ? (
                        <div className="border-t border-white/10 bg-background px-4 py-4 space-y-3" data-testid="concierge-handoff-form">
                            <div className="flex items-center justify-between">
                                <div className="text-sm text-foreground font-semibold flex items-center gap-2">
                                    <Flag size={14} className="text-amber-300" /> Talk to a human
                                </div>
                                <button
                                    data-testid="concierge-handoff-cancel"
                                    onClick={() => setHandoffOpen(false)}
                                    className="text-xs text-muted-foreground hover:text-foreground"
                                >
                                    Cancel
                                </button>
                            </div>
                            <input
                                data-testid="concierge-handoff-email"
                                type="email"
                                value={handoffEmail}
                                onChange={(e) => setHandoffEmail(e.target.value)}
                                placeholder="Your email (optional)"
                                className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-xs text-foreground placeholder:text-muted-foreground focus:border-[#FFD700]/60 focus:outline-none"
                            />
                            <input
                                data-testid="concierge-handoff-phone"
                                type="tel"
                                value={handoffPhone}
                                onChange={(e) => setHandoffPhone(e.target.value)}
                                placeholder="Phone / WhatsApp (optional)"
                                className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-xs text-foreground placeholder:text-muted-foreground focus:border-[#FFD700]/60 focus:outline-none"
                            />
                            <textarea
                                data-testid="concierge-handoff-question"
                                value={handoffQuestion}
                                onChange={(e) => setHandoffQuestion(e.target.value)}
                                placeholder="What couldn't the Concierge help with? Admin will reply directly."
                                rows={3}
                                maxLength={1500}
                                className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-xs text-foreground placeholder:text-muted-foreground focus:border-[#FFD700]/60 focus:outline-none resize-none"
                            />
                            <button
                                data-testid="concierge-handoff-submit"
                                onClick={submitHandoff}
                                disabled={handoffSending}
                                className="w-full px-4 py-2.5 rounded-full bg-[#FFD700] text-black text-[11px] uppercase tracking-[0.22em] font-semibold hover:bg-amber-300 disabled:opacity-50 transition"
                            >
                                {handoffSending ? "Raising ticket..." : "Raise ticket"}
                            </button>
                        </div>
                    ) : (
                        <>
                            {hasChat && !handoffDone && (
                                <div className="px-3 pt-2 bg-background">
                                    <button
                                        data-testid="concierge-handoff-btn"
                                        onClick={() => {
                                            setHandoffQuestion(messages.filter((m) => m.role === "user").slice(-1)[0]?.content || "");
                                            setHandoffOpen(true);
                                        }}
                                        className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-full border text-[10px] uppercase tracking-[0.22em] transition-colors ${
                                            suggestHandoff
                                                ? "border-amber-500/60 text-amber-300 bg-amber-500/10 hover:bg-amber-500/20"
                                                : "border-white/10 text-muted-foreground hover:text-foreground hover:bg-white/5"
                                        }`}
                                    >
                                        <Flag size={11} />
                                        {suggestHandoff ? "Talk to a human now" : "Talk to a human"}
                                    </button>
                                </div>
                            )}
                            {handoffDone && (
                                <div className="px-3 pt-2 bg-background">
                                    <div
                                        data-testid="concierge-handoff-done"
                                        className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-[10px] uppercase tracking-[0.22em]"
                                    >
                                        <Check size={11} /> Ticket raised · admin will reply
                                    </div>
                                </div>
                            )}
                            <form
                                onSubmit={(e) => { e.preventDefault(); send(); }}
                                className="border-t border-white/10 bg-background px-3 py-3 flex items-center gap-2 mt-2"
                            >
                                <input
                                    data-testid="concierge-input"
                                    type="text"
                                    value={draft}
                                    onChange={(e) => setDraft(e.target.value)}
                                    placeholder="Ask about a service, price, booking..."
                                    disabled={sending}
                                    className="flex-1 bg-black/40 border border-white/10 rounded-full px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:border-[#FFD700]/60 focus:outline-none"
                                    maxLength={1500}
                                />
                                <button
                                    data-testid="concierge-send"
                                    type="submit"
                                    disabled={!draft.trim() || sending}
                                    className="w-10 h-10 rounded-full bg-[#FFD700] text-black grid place-items-center disabled:opacity-40 disabled:cursor-not-allowed hover:scale-105 active:scale-95 transition-all"
                                    aria-label="Send message"
                                >
                                    <Send size={16} />
                                </button>
                            </form>
                        </>
                    )}
                </div>
            )}
        </>
    );
}
